import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, ChevronDown, Globe, User as UserIcon } from 'lucide-react';
import { reportTemplatesApi, type Template } from '../lib/reportTemplatesApi';

export type { Template };

interface TemplateSelectorProps {
  darkMode: boolean;
  onTemplateSelect: (template: Template | null) => void;
  hideLabel?: boolean;
}

export function TemplateSelector({ darkMode, onTemplateSelect, hideLabel }: TemplateSelectorProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const [loading, setLoading] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (isOpen && templates.length === 0 && !loading) {
      setLoading(true);
      reportTemplatesApi
        .list()
        .then(setTemplates)
        .catch(() => setTemplates([]))
        .finally(() => setLoading(false));
    }
  }, [isOpen, templates.length, loading]);

  const updateDropdownPos = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updateDropdownPos();
      window.addEventListener('resize', updateDropdownPos);
      window.addEventListener('scroll', updateDropdownPos, true);
      return () => {
        window.removeEventListener('resize', updateDropdownPos);
        window.removeEventListener('scroll', updateDropdownPos, true);
      };
    }
  }, [isOpen, updateDropdownPos]);

  const handleSelect = (t: Template) => {
    setSelectedTemplate(t);
    onTemplateSelect(t);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    setSelectedTemplate(null);
    onTemplateSelect(null);
  };

  const filtered = templates.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase()),
  );
  const systemList = filtered.filter((t) => t.scope === 'system');
  const userList = filtered.filter((t) => t.scope === 'user');

  const triggerCls = darkMode
    ? 'bg-slate-700 border-slate-600 text-white'
    : 'bg-white border-slate-300';
  const popoverCls = darkMode
    ? 'bg-slate-800 border-slate-700 text-white'
    : 'bg-white border-slate-200';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  return (
    <div>
      {!hideLabel && (
        <label className={`block text-xs font-medium mb-1 ${muted}`}>
          Vorlage anwenden (optional)
        </label>
      )}
      <div className="relative">
        <button
          ref={triggerRef}
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          className={`w-full px-3 py-2 border rounded-lg text-sm flex items-center justify-between gap-2 ${triggerCls}`}
        >
          <span className="truncate">
            {selectedTemplate ? selectedTemplate.name : 'Keine Vorlage'}
          </span>
          <div className="flex items-center gap-1">
            {selectedTemplate && (
              <X
                className="w-3.5 h-3.5 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
              />
            )}
            <ChevronDown className="w-3.5 h-3.5" />
          </div>
        </button>

        {isOpen &&
          createPortal(
            <div
              className={`fixed z-50 rounded-lg border shadow-lg max-h-72 overflow-y-auto ${popoverCls}`}
              style={{
                top: dropdownPos.top,
                left: dropdownPos.left,
                width: dropdownPos.width,
              }}
            >
              <div className={`p-2 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <div className="flex items-center gap-1">
                  <Search className="w-3.5 h-3.5 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Suchen…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="flex-1 bg-transparent outline-none text-sm"
                    autoFocus
                  />
                </div>
              </div>

              {loading ? (
                <p className={`p-3 text-sm ${muted}`}>Lädt…</p>
              ) : filtered.length === 0 ? (
                <p className={`p-3 text-sm italic ${muted}`}>Keine Vorlage gefunden</p>
              ) : (
                <>
                  {systemList.length > 0 && (
                    <div>
                      <p className={`px-3 py-1 text-xs font-semibold uppercase tracking-wide ${muted}`}>System</p>
                      {systemList.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => handleSelect(t)}
                          className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                        >
                          <Globe className="w-3 h-3 text-blue-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium truncate">{t.name}</p>
                            {t.description && (
                              <p className={`text-xs truncate ${muted}`}>{t.description}</p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {userList.length > 0 && (
                    <div>
                      <p className={`px-3 py-1 text-xs font-semibold uppercase tracking-wide ${muted}`}>Meine</p>
                      {userList.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => handleSelect(t)}
                          className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`}
                        >
                          <UserIcon className="w-3 h-3 text-slate-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium truncate">{t.name}</p>
                            {t.description && (
                              <p className={`text-xs truncate ${muted}`}>{t.description}</p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}
